import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { createLoginRecord } from "../../api/entity/login_record";
import { useApoAuth } from "..";

/**
 * Hook to create a login record once per page load.
 *
 * Business Logic:
 * This hook tracks user login activity by creating a LoginRecord entry in the CRM system.
 * The record is created only if:
 * - No login record exists for today, OR
 * - A login record exists but has a different token
 *
 * Additionally, when a new record is created, it updates the user's last_login timestamp.
 *
 * Conditions for execution:
 * - User is authenticated
 * - Username is available
 * - User is B2B (is_b2c === "false")
 * - It hasn't been called yet in this page load
 */
export function useLoginRecord() {
  const { user, isAuthenticated, getIdTokenClaims } = useApoAuth();
  const hasCalledRef = useRef(false);

  const mutation = useMutation({
    mutationFn: async (data: { username: string }) => {
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims || !("__raw" in tokenClaims)) {
        throw new Error("Unable to get authentication token");
      }
      const token = (tokenClaims as { __raw: string }).__raw;
      return createLoginRecord(data, token);
    },
    retry: false, // Don't retry on failure
    onError: (error) => {
      // Silently fail - we don't want login record creation to break the app
      console.error("Failed to create login record:", error);
    },
  });

  useEffect(() => {
    // Only call once per page load
    if (hasCalledRef.current) return;

    // Only proceed if user is authenticated and username is available
    if (!isAuthenticated || !user) return;

    // Check if user is B2B (is_b2c === "false")
    const sisitripData = user["https://sisitrip.com"] as
      | {
          mark?: {
            is_b2c?: string;
          };
        }
      | undefined;
    const isB2c = sisitripData?.mark?.is_b2c;
    if (isB2c?.toString() !== "false") return;

    // Get username from user object (use sub)
    const username = user.sub;
    if (!username) return;

    // Mark as called before making the request to prevent duplicate calls
    hasCalledRef.current = true;

    // Call the mutation
    mutation.mutate({ username });
  }, [isAuthenticated, user]);

  return mutation;
}
